import { useCallback } from "react";
import { useSelector } from "react-redux";

import { FormInstance } from "antd/es/form";

import CustomFieldValue from "@mapmycustomers/shared/types/customField/CustomFieldValue";
import { Activity } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import localSettings from "@app/config/LocalSettings";
import { ActivityPayload, FieldValidationErrors } from "@app/store/api/ApiService";
import { doesOrganizationSupportMultiContactActivities } from "@app/store/iam";
import HideAction from "@app/types/HideCallback";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import { createActivity } from "../store/actions";
import FormValues from "../types/FormValues";

import getActivityPayloadFromCreationForm from "./getActivityPayloadFromCreationForm";

const useCreateCallback = (
  activityTypes: ActivityType[],
  createActivityRequest: typeof createActivity.request,
  form: FormInstance<FormValues>,
  formLayout: FormLayout | undefined,
  onHide: HideAction<Activity>,
  analyticIssuer?: AnalyticsService,
  handleFieldValidationErrors?: (fieldErrors: FieldValidationErrors[]) => void
) => {
  const isMultiContactEnabled = useSelector(doesOrganizationSupportMultiContactActivities);

  return useCallback(() => {
    if (!formLayout) {
      return;
    }
    const values = form.getFieldsValue(true);

    const activity: ActivityPayload = getActivityPayloadFromCreationForm(
      values,
      activityTypes,
      isMultiContactEnabled
    );

    const customFieldsValues = values.customFields
      ? Object.values<CustomFieldValue>(values.customFields).filter(isDefined)
      : [];

    localSettings.setActivityCreationNotifyPreferences(values.reminders);

    createActivityRequest({
      activity,
      callback: onHide,
      customFieldsValues,
      fieldsValueValidateCallback: handleFieldValidationErrors,
      layoutId: formLayout.id,
    });
    analyticIssuer?.feature(["Activity Created"], {
      activityId: activity.id,
      activityName: activity.name,
    });
  }, [
    activityTypes,
    analyticIssuer,
    createActivityRequest,
    form,
    formLayout,
    handleFieldValidationErrors,
    isMultiContactEnabled,
    onHide,
  ]);
};

export default useCreateCallback;
